import { clsx } from "clsx";
import { Check } from "lucide-react";
import type { PointerEvent } from "react";
import { useCallback } from "react";
import { useDropdownContext } from "../Dropdown";
import type { ISelectOption } from "../Select/types";
import { Text } from "../Text";
import { TruncatedContent } from "../TruncatedContent";
import { useComboBoxContext } from "./context";

type ComboBoxItemProps = {
  option: ISelectOption;
  index: number;
};

export const ComboBoxItem = ({ option, index }: ComboBoxItemProps) => {
  const { value, onRenderOption, onSelect, contentId, multiple } =
    useComboBoxContext();
  const { navigationRef, activeIndex } = useDropdownContext();

  const optionId = `${contentId}-option-${index}`;

  const isSelected = multiple
    ? (Array.isArray(value) ? value : []).some((v) => v.id === option.id)
    : (value as ISelectOption)?.id === option.id;

  const isActive = activeIndex === index;

  const handlePointerDown = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;

      e.preventDefault();
      e.stopPropagation();

      if (option.disabled) return;

      onSelect(option);
    },
    [option, onSelect],
  );

  const rootClass: string = clsx(
    "nuco-select__option",
    isSelected && "nuco-select__option--selected",
    isActive && "nuco-select__option--active",
    option.disabled && "nuco-select__option--disabled",
  );

  return (
    <div
      id={optionId}
      role="option"
      aria-selected={isSelected}
      aria-disabled={option.disabled}
      className={rootClass}
      ref={navigationRef(index)}
      onPointerDown={handlePointerDown}
      tabIndex={-1}
      data-selected={isSelected ? "" : undefined}
      data-highlighted={isActive ? "" : undefined}
    >
      {onRenderOption ? (
        onRenderOption(option)
      ) : (
        <>
          {option.icon && <div className="flex-shrink-0">{option.icon}</div>}
          <TruncatedContent
            className="nuco-text nuco-text--md"
            content={option.text}
          />
          {option.description && (
            <Text className="nuco-select__option-description">
              {option.description}
            </Text>
          )}
        </>
      )}

      <div className="nuco-select__option-right">
        {isSelected && (
          <div className="nuco-select__option-checkMark">
            <Check className="nuco-select__option-right-icon" size="1rem" />
          </div>
        )}
      </div>
    </div>
  );
};
